import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Upload } from 'lucide-react';
import { EntityLinkSelector } from '@/components/shared/EntityLinkSelector';
import type { EntityType } from '@/types/link';

interface SelectedEntity {
  id: string;
  type: EntityType;
  name: string;
}

interface EvidenceCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: any, links?: SelectedEntity[]) => void;
}

export function EvidenceCreateDialog({ open, onOpenChange, onSave }: EvidenceCreateDialogProps) {
  const [formData, setFormData] = useState({
    name: '',
    type: 'document',
    notes: '',
  });
  const [selectedLinks, setSelectedLinks] = useState<SelectedEntity[]>([]);

  const handleSubmit = () => {
    onSave(formData, selectedLinks);
    setFormData({ name: '', type: 'document', notes: '' });
    setSelectedLinks([]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Evidence</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Evidence name"
            />
          </div>
          <div>
            <Label htmlFor="type">Type</Label>
            <Input
              id="type"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              placeholder="Document type"
            />
          </div>
          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes"
            />
          </div>

          <EntityLinkSelector
            allowedTypes={['asset', 'system', 'risk', 'control', 'supplier', 'incident', 'task']}
            onLinksChange={setSelectedLinks}
            initialLinks={selectedLinks}
          />
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!formData.name}>
            <Upload className="w-4 h-4 mr-2" />
            Add Evidence
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
